import { cache } from 'react';
import {
    cacheProfile,
    cachedProfile,
    getAuthToken,
    getRefreshToken,
    saveAuthSession,
} from '@/utils/profile/authSession';

interface User {
    id: number;
    firstName: string;
    lastName?: string;
    email?: string;
    mobile: string;
}

interface VerifyUserResponse {
    success: boolean;
    data: { user: User } | null;
    message: string;
    error: any;
    /** Set by /api/auth/getUser when the access token had expired and the refresh token renewed it. */
    newToken?: string;
}

/**
 * null = not logged in (no token, or WpInterface rejected both tokens).
 * A network / server error keeps the last known profile instead of showing the user as logged out.
 */
export const getUser = cache(async (): Promise<VerifyUserResponse | null> => {
    const token = getAuthToken();
    const refreshToken = getRefreshToken();
    if (!token && !refreshToken) {
        return null;
    }

    try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        const response = await fetch('/api/auth/getUser', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token, refreshToken }),
            signal: controller.signal,
        }).finally(() => clearTimeout(timer));

        if (response.status === 401) {
            return null;
        }
        if (!response.ok) {
            throw new Error(`getUser failed (status: ${response.status})`);
        }

        const data: VerifyUserResponse = await response.json();
        if (!data.success || !data.data?.user) {
            return null;
        }
        if (data.newToken) {
            saveAuthSession(data.newToken);
        }
        cacheProfile(data.data.user);

        return data;
    } catch (error) {
        console.error('Error verifying user:', error);
        const cached = cachedProfile<User>();
        if (cached) {
            return { success: true, data: { user: cached }, message: 'cached', error: null };
        }
        throw error;
    }
});
